import { useState, useEffect, useCallback } from 'react'
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom'
import useAnalytics from './hooks/useAnalytics'
import AnalyticsConsentBanner from './components/AnalyticsConsentBanner.jsx'

// --- CRITICAL: Problematic Device Recovery (Xiaomi/Samsung Fix) ---
// This runs immediately, even if React or Babel fails later.
(function() {
    const ua = (navigator.userAgent || navigator.vendor || window.opera || '').toLowerCase();
    const isXiaomi = ua.includes('xiaomi') || ua.includes('redmi') || ua.includes('poco') || ua.includes('hyperos') || ua.includes('miui');
    const isSamsungOld = ua.includes('samsung') && (ua.includes('android 12') || ua.includes('android 13'));
    
    // 1. Immediate Cache Cleanup for stuck Xiaomi devices
    if (isXiaomi && !sessionStorage.getItem('xiaomi_recovered')) {
        console.log('[Recovery] Xiaomi detected, performing session-start cleanup');
        if ('caches' in window) {
            caches.keys().then(names => {
                names.forEach(name => caches.delete(name));
            });
        }
        sessionStorage.setItem('xiaomi_recovered', 'true');
    }

    // 2. Failsafe Timeout: If the screen is still blank after 8 seconds, force a hard reload
    setTimeout(() => {
        const root = document.getElementById('root');
        // If root is empty or still has the loading spinner after 8s, something crashed
        const isStillLoading = root && (root.innerHTML.includes('Loading Miners Market') || root.innerHTML.trim() === '');
        
        if (isStillLoading) {
            console.warn('[Recovery] App failed to mount in time. Triggering hard reset.');
            
            // Try one-time emergency redirect
            if (!window.location.search.includes('retry=true')) {
                const url = new URL(window.location.href);
                url.searchParams.set('retry', 'true');
                url.searchParams.set('t', Date.now());
                window.location.replace(url.href);
            } else {
                // If already retried, show the manual "Clear Cache" UI
                if (root) {
                    root.innerHTML = `
                        <div style="background:#111827;color:white;padding:40px;text-align:center;font-family:sans-serif;height:100vh;display:flex;flex-direction:column;justify-content:center;">
                            <h2 style="color:#ef4444">Connection Issue</h2>
                            <p style="color:#9ca3af">The app is taking too long to load on this device.</p>
                            <button onclick="localStorage.clear();sessionStorage.clear();location.reload(true);"
                                    style="background:#3b82f6;color:white;border:none;padding:12px 24px;border-radius:8px;margin-top:20px;font-weight:bold;">
                                Reset App & Try Again
                            </button>
                            <p style="margin-top:20px;font-size:12px;color:#4b5563">Tip: Ensure you are using the latest version of Chrome.</p>
                        </div>
                    `;
                }
            }
        }
    }, 8000);
})();

// --- Category constants for marketplace pages ---
const EQUIPMENT_CATEGORIES = ['construction-equipment', 'plant', 'surface-equipment', 'underground-equipment', 'transportation'];
const CONSUMABLES_CATEGORIES = ['consumables'];
const MISC_CATEGORIES = ['bearings', 'filters', 'conveyor-belts', 'greases', 'kg-pps', 'diesel'];
const COMPONENTS_CATEGORIES = ['construction-equipment', 'underground-equipment', 'transportation', 'surface-equipment', 'plant', 'compressor-fleet-management'];
const RENTAL_CATEGORIES = ['rental-equipment'];
const COMMERCIAL_CATEGORIES = ['mines', 'ore-sales', 'refurbishing-services'];

// A component to guard routes that require a logged-in user.
function ProtectedRoute({ user, allowedRoles, children }) {
    if (!user || !user.roles || !user.activeRole) {
        sessionStorage.removeItem('minersMarketUser');
        return <Navigate to="/" replace />;
    }
    if (allowedRoles && !allowedRoles.includes(user.activeRole)) {
        const defaultPath = user.roles.includes('seller') || user.roles.includes('admin') ? '/dashboard' : '/marketplace';
        return <Navigate to={defaultPath} replace />;
    }
    return children;
}
import CategoryFilter from './components/CategoryFilter.jsx'
import ProductCard from './components/ProductCard.jsx'
import RecommendedSuppliers from './components/RecommendedSuppliers.jsx'
import ConsumablesFilterBoxes from './components/ConsumablesFilterBoxes.jsx'
import LoginModal from './components/LoginModal.jsx'
import BottomNavBar from './components/BottomNavBar.jsx'
import MobileMenu from './components/MobileMenu.jsx'
import Header from './components/Header.jsx'
import Footer from './components/Footer.jsx'
import HomePage from './pages/HomePage.jsx'
import MarketplacePage from './pages/MarketplacePage.jsx'
import ProfilePage from './pages/ProfilePage.jsx'
import AppLandingPage from './pages/AppLandingPage.jsx'
import AdBlocksPage from './pages/AdBlocksPage.jsx'
import BasketPage from './pages/BasketPage.jsx'
import BearingProductPage from './pages/BearingProductPage.jsx'
import CategoriesPage from './pages/CategoriesPage.jsx'
import DieselQuotesPage from './pages/DieselQuotesPage.jsx'
import FinanceFormPage from './pages/FinanceFormPage.jsx'
import KgAnalyticsPage from './pages/KgAnalyticsPage.jsx'
import KpInsurancePage from './pages/KpInsurancePage.jsx'
import KpInsuranceAdminPage from './pages/KpInsuranceAdminPage.jsx'
import LabourPage from './pages/LabourPage.jsx'
import MarketingPage from './pages/MarketingPage.jsx'
import PasswordResetPage from './pages/PasswordResetPage.jsx'
import PlatformFeesPage from './pages/PlatformFeesPage.jsx'
import ProductListPage from './pages/ProductListPage.jsx'
import RebuildMarketplacePage from './pages/RebuildMarketplacePage.jsx'
import SavedItemsPage from './pages/SavedItemsPage.jsx'
import SHEQPage from './pages/SHEQPage.jsx'
import DrillCatalogPage from './pages/DrillCatalogPage.jsx'
import TestWatermarkPage from './pages/TestWatermarkPage.jsx'
import UserPathsPage from './pages/UserPathsPage.jsx'
import WotsMarketplacePage from './pages/WotsMarketplacePage.jsx'
import SubscribePrompt from './components/SubscribePrompt.jsx'
import ChatbaseIframe from './components/ChatbaseIframe.jsx'
import InstallPwa from './components/InstallPwa.jsx'
import JumpToTopButton from './components/JumpToTopButton.jsx'
import AdBlockMediaViewer from './components/AdBlockMediaViewer.jsx'
import FullScreenProductModal from './components/FullScreenProductModal.jsx'
import InquiryModal from './components/InquiryModal.jsx'
import DieselQuoteModal from './components/DieselQuoteModal.jsx'
import ContactAdminModal from './components/ContactAdminModal.jsx'
import QuoteConfirmationModal from './components/QuoteConfirmationModal.jsx'
import ImageBlurEditor from './components/ImageBlurEditor.jsx'
import ConfirmationModal from './components/ConfirmationModal.jsx'
import NotificationBell from './components/NotificationBell.jsx'
import NotificationManager from './components/NotificationManager.jsx'
import AccountDeletionModal from './components/AccountDeletionModal.jsx'
import AdminDashboard from './components/AdminDashboard.jsx'
import SellerDashboard from './components/SellerDashboard.jsx'
import BuyerDashboard from './components/BuyerDashboard.jsx'
import KgAnalytics from './components/KgAnalytics.jsx'
import EditListingModal from './components/EditListingModal.jsx'
import AddItemWizard from './components/AddItemWizard.jsx'
import RebuildMatrixForm from './components/RebuildMatrixForm.jsx'
import BasketQuoteItemsModal from './components/BasketQuoteItemsModal.jsx'
import BankingDetailsForm from './components/BankingDetailsForm.jsx'
import AccordionSection from './components/AccordionSection.jsx'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import Pagination from './components/Pagination.jsx'
import data from './data/suppliers.jsx'
import DashboardPage from './pages/DashboardPage.jsx'
import MaintenancePage from './pages/MaintenancePage.jsx'
import SupplierAnalyticsPage from './pages/SupplierAnalyticsPage.jsx'

function App() {
  const [user, setUser] = useState(() => {
    try {
      const saved = sessionStorage.getItem('minersMarketUser')
      return saved ? JSON.parse(saved) : null
    } catch (e) {
      return null
    }
  })
  const [basket, setBasket] = useState(() => {
    try {
      const saved = sessionStorage.getItem('minersMarketBasket')
      return saved ? JSON.parse(saved) : []
    } catch (e) {
      return []
    }
  })
  const [savedItems, setSavedItems] = useState(() => {
    try {
      const saved = sessionStorage.getItem('minersMarketSavedItems')
      return saved ? JSON.parse(saved) : []
    } catch (e) {
      return []
    }
  })
  const [showLogin, setShowLogin] = useState(false)
  const [authModalMode, setAuthModalMode] = useState('login')
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [isFetchingProduct, setIsFetchingProduct] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isAppMenuOpen, setIsAppMenuOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState('home')
  const [showSubscribePrompt, setShowSubscribePrompt] = useState(false)
  const [showInstallPwa, setShowInstallPwa] = useState(false)
  const [showChatIframe, setShowChatIframe] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [adBlockMedia, setAdBlockMedia] = useState(null)
  const [showImageBlurEditor, setShowImageBlurEditor] = useState(false)
  const [imageToEdit, setImageToEdit] = useState(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [confirmationData, setConfirmationData] = useState(null)
  const [showAccountDeletion, setShowAccountDeletion] = useState(false)
  const [showInquiry, setShowInquiry] = useState(false)
  const [inquiryProduct, setInquiryProduct] = useState(null)
  const [showDieselQuote, setShowDieselQuote] = useState(false)
  const [dieselQuoteProduct, setDieselQuoteProduct] = useState(null)
  const [showContactAdmin, setShowContactAdmin] = useState(false)
  const [contactAdminProduct, setContactAdminProduct] = useState(null)
  const [showQuoteConfirm, setShowQuoteConfirm] = useState(false)
  const [quoteConfirmData, setQuoteConfirmData] = useState(null)

  const location = useLocation()
  const navigate = useNavigate()

  const { trackEvent, consent, grantConsent, declineConsent, withdrawConsent, resetVisitor } = useAnalytics()
  const [consentBannerOpen, setConsentBannerOpen] = useState(!consent)

  // Track page visits on route change
  useEffect(() => {
    if (consent === 'granted') {
      trackEvent('page_visited', {
        page_path: location.pathname,
        page_title: document.title || '',
      })
    }
  }, [location, consent, trackEvent])

  const saveUserToStorage = (userData) => {
    if (userData) {
      sessionStorage.setItem('minersMarketUser', JSON.stringify(userData))
    } else {
      sessionStorage.removeItem('minersMarketUser')
    }
  }

  // Clear invalid user sessions (missing activeRole from old login responses)
  useEffect(() => {
    if (user && (!user.roles || !user.activeRole)) {
      sessionStorage.removeItem('minersMarketUser')
      setUser(null)
    }
  }, [user])

  useEffect(() => {
    sessionStorage.setItem('minersMarketUser', JSON.stringify(user))
  }, [user])

  useEffect(() => {
    sessionStorage.setItem('minersMarketBasket', JSON.stringify(basket))
  }, [basket])

  useEffect(() => {
    sessionStorage.setItem('minersMarketSavedItems', JSON.stringify(savedItems))
  }, [savedItems])

  useEffect(() => {
    if (selectedProduct) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
    return () => { document.body.style.overflow = 'auto' }
  }, [selectedProduct])

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setShowInstallPwa(true)
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  }, [])

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/app/service-worker.js', { updateViaCache: 'none' }).then(registration => {
        console.log('SW registered: ', registration)
        // When a new SW finishes installing, tell it to skip waiting
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                newWorker.postMessage({ type: 'SKIP_WAITING' })
              }
            })
          }
        })
      }).catch(registrationError => {
        console.log('SW registration failed: ', registrationError)
      })

      // Listen for SW messages and force reload when a new version activates
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log('[SW] New version activated, reloading page')
          window.location.reload()
        }
      })
    }
  }, [])

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch('/api/get_config.php')
        const contentType = response.headers.get('content-type')
        if (!response.ok || !contentType || !contentType.includes('application/json')) {
          throw new Error('Invalid config response')
        }
        const config = await response.json()
        window.APP_CONFIG = config
        console.log('App configuration loaded:', config)
      } catch (error) {
        console.error('Error fetching app configuration:', error)
        window.APP_CONFIG = { platformFeePercentage: 0.06, platformAdminUserId: 35 }
      }
    }
    fetchConfig()
  }, [])

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      const timer = setTimeout(() => {
        setShowSubscribePrompt(true)
      }, 30000)
      return () => clearTimeout(timer)
    }
  }, [])

  // Handle custom events for signup/login flow from AppLandingPage
  useEffect(() => {
    const handleShowSignupModal = (event) => {
      console.log('App.jsx: handleShowSignupModal called', event.detail);
      const { sellerFlow } = event.detail || {};
      setAuthModalMode('signup');
      setShowLogin(true);
    };

    const handleShowLoginModal = (event) => {
      console.log('App.jsx: handleShowLoginModal called', event.detail);
      setAuthModalMode('login');
      setShowLogin(true);
    };

    window.addEventListener('showSignupModal', handleShowSignupModal);
    window.addEventListener('showLoginModal', handleShowLoginModal);
    return () => {
      window.removeEventListener('showSignupModal', handleShowSignupModal);
      window.removeEventListener('showLoginModal', handleShowLoginModal);
    };
  }, []);

  const handleLogin = (userData) => {
    // Handle both 'role' (string from Google auth) and 'roles' (array from regular auth)
    let roles;
    if (userData.roles && Array.isArray(userData.roles)) {
        roles = userData.roles;
    } else if (userData.role) {
        roles = [userData.role];
    } else {
        roles = ['buyer']; // Default fallback
    }

    // Determine the primary active role on login. Priority: admin > seller > buyer.
    const activeRole = ['admin', 'seller'].find(r => roles.includes(r)) || 'buyer';
    const userWithActiveRole = { ...userData, roles, activeRole };

    // Persist user to sessionStorage so it survives refresh
    try {
        sessionStorage.setItem('minersMarketUser', JSON.stringify(userWithActiveRole));
    } catch (e) {
        // ignore storage errors
    }

    setUser(userWithActiveRole);
    setShowLogin(false);
    resetVisitor();
    if (consent === 'granted') {
      trackEvent('session_started', {});
    }
  }

  const handleLogout = () => {
    setUser(null)
    setBasket([])
    setSavedItems([])
    sessionStorage.removeItem('minersMarketUser')
    sessionStorage.removeItem('minersMarketBasket')
    sessionStorage.removeItem('minersMarketSavedItems')
    sessionStorage.removeItem('mm_session_uuid')
    resetVisitor()
    if (consent === 'granted') {
      trackEvent('session_expired', {})
    }
  }

  // Resolve stock for both legacy {size: qty} and {size: {qty, price}} formats.
  const getConsumableStock = (product, size) => {
    let sizes = product.size
    if (typeof sizes === 'string') {
      try { sizes = JSON.parse(sizes) } catch { return 0 }
    }
    if (!sizes || typeof sizes !== 'object' || Array.isArray(sizes)) return 0
    const entry = sizes[size]
    const stock = entry && typeof entry === 'object' ? entry.qty : entry
    const value = Number(stock)
    return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0
  }

  const addToBasket = (product, quantity = 1, selectedSizes = null) => {
    setBasket(prev => {
      const existing = prev.find(item => item.id === product.id && item.listingType === product.listingType)
      const isSizedConsumable = product.listingType === 'consumable' &&
        selectedSizes && typeof selectedSizes === 'object' && !Array.isArray(selectedSizes)

      if (isSizedConsumable) {
        const merged = { ...(existing?.selectedSizes || {}) }
        for (const [size, rawQty] of Object.entries(selectedSizes)) {
          const requested = Number(rawQty)
          if (!Number.isInteger(requested) || requested <= 0) continue
          const stock = getConsumableStock(product, size)
          if (stock <= 0) continue
          const previous = Number(merged[size]) || 0
          merged[size] = Math.min(stock, previous + requested)
        }
        const normalizedSizes = Object.fromEntries(
          Object.entries(merged).filter(([, qty]) => Number(qty) > 0)
        )
        const totalQty = Object.values(normalizedSizes).reduce((sum, qty) => sum + Number(qty), 0)
        if (totalQty === 0) return prev
        if (existing) {
          const result = prev.map(item =>
            item.id === product.id && item.listingType === product.listingType
              ? { ...item, quantity: totalQty, selectedSizes: normalizedSizes }
              : item
          )
          if (consent === 'granted') {
            trackEvent('product_added_to_basket', {
              productId: product.id,
              listingType: product.listingType,
              listingNumber: product.listing_number || '',
              productName: product.name || '',
              categoryId: product.category_id ?? null,
              categoryName: product.category_name || '',
              eventData: {
                sizes: normalizedSizes,
                quantity: totalQty,
                unitPrice: product.price ?? null,
              },
            })
          }
          return result
        }
        const result = [...prev, { ...product, quantity: totalQty, selectedSizes: normalizedSizes }]
        if (consent === 'granted') {
          trackEvent('product_added_to_basket', {
            productId: product.id,
            listingType: product.listingType,
            listingNumber: product.listing_number || '',
            productName: product.name || '',
            categoryId: product.category_id ?? null,
            categoryName: product.category_name || '',
            eventData: {
              sizes: normalizedSizes,
              quantity: totalQty,
              unitPrice: product.price ?? null,
            },
          })
        }
        return result
      }

      // Sized consumables must be added through size selection, never as a bare quantity.
      if (product.listingType === 'consumable' && existing?.selectedSizes &&
          Object.keys(existing.selectedSizes).length > 0) return prev

      if (existing) {
        const newQty = existing.quantity + quantity
        const result = prev.map(item =>
          item.id === product.id && item.listingType === product.listingType
            ? { ...item, quantity: newQty }
            : item
        )
        if (consent === 'granted') {
          trackEvent('product_added_to_basket', {
            productId: product.id,
            listingType: product.listingType,
            listingNumber: product.listing_number || '',
            productName: product.name || '',
            categoryId: product.category_id ?? null,
            categoryName: product.category_name || '',
            eventData: {
              sizes: existing.selectedSizes || null,
              quantity: newQty,
              unitPrice: product.price ?? null,
            },
          })
        }
        return result
      }
      const result = [...prev, { ...product, quantity, selectedSizes }]
      if (consent === 'granted') {
        trackEvent('product_added_to_basket', {
          productId: product.id,
          listingType: product.listingType,
          listingNumber: product.listing_number || '',
          productName: product.name || '',
          categoryId: product.category_id ?? null,
          categoryName: product.category_name || '',
          eventData: {
            sizes: selectedSizes || null,
            quantity,
            unitPrice: product.price ?? null,
          },
        })
      }
      return result
    })
  }

  const updateBasketQuantity = (productId, listingType, quantity) => {
    setBasket(prev => prev.flatMap(item => {
      if (item.id !== productId || item.listingType !== listingType) return [item]
      // A scalar quantity cannot describe which size changed. Keep sizes synchronized.
      if (item.listingType === 'consumable' && item.selectedSizes &&
          Object.keys(item.selectedSizes).length > 0) return [item]
      const result = quantity > 0 ? [{ ...item, quantity }] : []
      if (consent === 'granted' && quantity > 0) {
        trackEvent('product_quantity_changed', {
          productId,
          listingType,
          listingNumber: item.listing_number || '',
          productName: item.name || '',
          eventData: {
            quantity,
            unitPrice: item.price ?? null,
          },
        })
      }
      return result
    }))
  }

  const removeFromBasket = (productId, listingType) => {
    const removed = basket.find(item => item.id === productId && item.listingType === listingType)
    setBasket(prev => prev.filter(item => !(item.id === productId && item.listingType === listingType)))
    if (consent === 'granted' && removed) {
      trackEvent('product_removed_from_basket', {
        productId,
        listingType,
        listingNumber: removed.listing_number || '',
        productName: removed.name || '',
        eventData: {
          quantity: removed.quantity,
          unitPrice: removed.price ?? null,
        },
      })
    }
  }

  const clearBasket = () => setBasket([])

  const toggleSaveItem = async (product) => {
    const wasSaved = savedItems.some(item => item.id === product.id && item.listingType === product.listingType)
    setSavedItems(prev => {
      const exists = prev.find(item => item.id === product.id && item.listingType === product.listingType)
      if (exists) {
        if (consent === 'granted') {
          trackEvent('product_unsaved', {
            productId: product.id,
            listingType: product.listingType,
            listingNumber: product.listing_number || '',
            productName: product.name || '',
            categoryId: product.category_id ?? null,
            categoryName: product.category_name || '',
          })
        }
        return prev.filter(item => !(item.id === product.id && item.listingType === product.listingType))
      }
      if (consent === 'granted') {
        trackEvent('product_saved', {
          productId: product.id,
          listingType: product.listingType,
          listingNumber: product.listing_number || '',
          productName: product.name || '',
          categoryId: product.category_id ?? null,
          categoryName: product.category_name || '',
        })
      }
      return [...prev, product]
    })

    // Persist to server for logged-in users.
    if (user && user.id) {
      try {
        await fetch('/api/toggle_saved_item.php', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_id: product.id, listing_type: product.listingType }),
        })
      } catch (e) {
        // Server sync failures must not interrupt save/unsave.
        console.warn('[SavedItems] server sync failed:', e.message)
      }
    }
  }

  const handleViewProduct = (product) => {
    setSelectedProduct(product)
  }

  const handleCloseProduct = () => {
    setSelectedProduct(null)
  }

  const handleViewProductById = useCallback(async (productId, listingType) => {
    if (isFetchingProduct) return
    setIsFetchingProduct(true)
    try {
      const response = await fetch(`/api/get_listing_details.php?id=${productId}&type=${listingType}`, { credentials: 'include' })
      if (!response.ok) {
        const errData = await response.json()
        throw new Error(errData.error || 'Failed to fetch product details.')
      }
      const productData = await response.json()
      let badgeType = 'standard'
      if (productData.listingType === 'wots') {
        badgeType = 'wots'
      } else if (productData.listing_market === 'miscellaneous') {
        badgeType = 'standard'
      }
      setSelectedProduct({ ...productData, badgeType })
    } catch (error) {
      console.error("Error fetching product by ID:", error)
      alert(`Error: ${error.message}`)
    } finally {
      setIsFetchingProduct(false)
    }
  }, [isFetchingProduct])

  const checkBatchStatus = useCallback(async (items) => {
    if (!items || items.length === 0) return []
    try {
      const response = await fetch('/api/check_batch_status.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
        credentials: 'include'
      })
      if (!response.ok) return []
      const data = await response.json()
      return data.results || []
    } catch (e) {
      console.error('Batch check failed', e)
      return []
    }
  }, [])

  const handleUserUpdate = (updatedData) => {
    setUser(prevUser => {
      const newUser = { ...prevUser, ...updatedData }
      saveUserToStorage(newUser)
      if (updatedData.status && updatedData.status === 'pending') {
        setTimeout(() => {
          handleLogout()
        }, 5000)
      }
      return newUser
    })
  }

  const handleSwitchRole = (newRole) => {
    if (user && user.roles.includes(newRole) && user.activeRole !== newRole) {
      const updatedUser = { ...user, activeRole: newRole }
      saveUserToStorage(updatedUser)
      setUser(updatedUser)
      navigate('/dashboard')
    }
  }

  const handleShowLogin = (mode = 'login') => {
    setAuthModalMode(mode)
    setShowLogin(true)
  }

  const handleShowInquiry = (product) => {
    if (!user) {
      handleShowLogin('login')
      return
    }
    setInquiryProduct(product)
    setShowInquiry(true)
  }

  const handleShowDieselQuote = (product) => {
    if (!user) {
      handleShowLogin('login')
      return
    }
    setDieselQuoteProduct(product)
    setShowDieselQuote(true)
  }

  const handleShowContactAdmin = (product) => {
    if (!user) {
      handleShowLogin('login')
      return
    }
    setContactAdminProduct(product)
    setShowContactAdmin(true)
  }

  const handleShowQuoteConfirm = (data) => {
    setQuoteConfirmData(data)
    setShowQuoteConfirm(true)
  }

  const handleInstallPwa = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      console.log(`User response: ${outcome}`)
      setDeferredPrompt(null)
      setShowInstallPwa(false)
    }
  }

  const handleDismissPwa = () => {
    setShowInstallPwa(false)
  }

  const handleRequestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        setShowSubscribePrompt(false)
        console.log('Notification permission granted')
      }
    }
  }

  const handleDismissSubscribe = () => {
    setShowSubscribePrompt(false)
  }

  const landingPagePaths = ['/', '/app-home', '/home', '/miners-market-main/home'];
  const marketplacePaths = ['/consumables', '/miscellaneous', '/rebuilds', '/rentals', '/commercial', '/search', '/components-and-spares', '/saved-items', '/bearing', '/equipment'];
  const alwaysShowHeaderRoutes = ['/dashboard', '/profile', '/supplier-analytics', '/admin'];
  const isLandingPage = landingPagePaths.includes(location.pathname);
  const isMarketplacePage = marketplacePaths.some(path => location.pathname.startsWith(path));
  const shouldShowHeader = !isMarketplacePage &&
                          alwaysShowHeaderRoutes.some(path => location.pathname.startsWith(path)) &&
                          location.pathname !== '/' &&
                          !location.pathname.startsWith('/categories');

return (
        <div className="flex flex-col min-h-screen pb-20 md:pb-0">
      {shouldShowHeader && (
        <Header
          user={user}
          basket={basket}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onShowLogin={() => handleShowLogin('login')}
          onLogout={handleLogout}
          onToggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />
      )}

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        user={user}
        onShowLogin={() => handleShowLogin('login')}
        onLogout={handleLogout}
      />

      <main className="flex-grow">
        <Routes>
          <Route path="/" element={
            user && (user.activeRole === 'seller' || user.activeRole === 'admin') ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <AppLandingPage
                onLogin={() => handleShowLogin('login')}
                onViewProduct={handleViewProduct}
                onAddToBasket={addToBasket}
                user={user}
                basket={basket}
              />
            )
          } />

          <Route path="/reset-password" element={<PasswordResetPage />} />

          <Route path="/app-home" element={
            <AppLandingPage
              onLogin={() => handleShowLogin('login')}
              onViewProduct={handleViewProduct}
              onAddToBasket={addToBasket}
              user={user}
              basket={basket}
            />
          } />

          <Route path="/home" element={
            <AppLandingPage
              onLogin={() => handleShowLogin('login')}
              onViewProduct={handleViewProduct}
              onAddToBasket={addToBasket}
              user={user}
              basket={basket}
            />
          } />

          <Route path="/miners-market-main/home" element={
            <AppLandingPage
              onLogin={() => handleShowLogin('login')}
              onViewProduct={handleViewProduct}
              onAddToBasket={addToBasket}
              user={user}
              basket={basket}
            />
          } />

          <Route path="/marketing" element={
            <MarketingPage
              onLogin={() => handleShowLogin('login')}
              onSignUp={() => handleShowLogin('signup')}
              onViewProduct={handleViewProduct}
              user={user}
              basket={basket}
              onAddToBasket={addToBasket}
              onUpdateBasketQuantity={updateBasketQuantity}
              onRemoveFromBasket={removeFromBasket}
            />
          } />

          <Route path="/categories" element={<CategoriesPage />} />

          <Route path="/search" element={
            <MarketplacePage
              key="search"
              pageTitle="Marketplace Search"
              pageSubtitle="Results from all categories"
              onViewProduct={handleViewProduct}
              user={user}
              basket={basket}
              onAddToBasket={addToBasket}
              onUpdateBasketQuantity={updateBasketQuantity}
              onRemoveFromBasket={removeFromBasket}
              onShowLogin={() => handleShowLogin('login')}
              onCheckBatchStatus={checkBatchStatus}
              savedItems={savedItems}
              onToggleSave={toggleSaveItem}
            />
          } />

          <Route path="/equipment" element={
            <MarketplacePage
              key="equipment"
              pageTitle="Equipment"
              pageSubtitle="Heavy-duty machinery for all your operational needs"
              category="construction-equipment"
              onViewProduct={handleViewProduct}
              user={user}
              basket={basket}
              onAddToBasket={addToBasket}
              onUpdateBasketQuantity={updateBasketQuantity}
              onRemoveFromBasket={removeFromBasket}
              onShowLogin={() => handleShowLogin('login')}
              onCheckBatchStatus={checkBatchStatus}
              onSwitchRole={handleSwitchRole}
              onLogout={handleLogout}
              allowedCategories={EQUIPMENT_CATEGORIES}
              savedItems={savedItems}
              onToggleSave={toggleSaveItem}
            />
          } />

          <Route path="/saved-items" element={
            <ProtectedRoute user={user} allowedRoles={['buyer', 'seller', 'admin']}>
              <SavedItemsPage
                onViewProduct={handleViewProduct}
                onToggleSave={toggleSaveItem}
                user={user}
                basket={basket}
                onAddToBasket={addToBasket}
                onUpdateBasketQuantity={updateBasketQuantity}
                onRemoveFromBasket={removeFromBasket}
                onShowLogin={() => handleShowLogin('login')}
                onLogout={handleLogout}
              />
            </ProtectedRoute>
          } />

          <Route path="/miscellaneous" element={
            <MarketplacePage
              key="miscellaneous"
              pageTitle="Miscellaneous"
              pageSubtitle="A collection of various parts and components"
              category="miscellaneous"
              onViewProduct={handleViewProduct}
              user={user}
              basket={basket}
              onAddToBasket={addToBasket}
              onUpdateBasketQuantity={updateBasketQuantity}
              onRemoveFromBasket={removeFromBasket}
              onShowLogin={() => handleShowLogin('login')}
              onCheckBatchStatus={checkBatchStatus}
              onSwitchRole={handleSwitchRole}
              onLogout={handleLogout}
              allowedCategories={MISC_CATEGORIES}
              savedItems={savedItems}
              onToggleSave={toggleSaveItem}
            />
          } />

          <Route path="/consumables" element={
            <MarketplacePage
              key="consumables"
              pageTitle="Consumables"
              pageSubtitle="Essential supplies and consumable items"
              category="consumables"
              onViewProduct={handleViewProduct}
              user={user}
              basket={basket}
              onAddToBasket={addToBasket}
              onUpdateBasketQuantity={updateBasketQuantity}
              onRemoveFromBasket={removeFromBasket}
              onShowLogin={() => handleShowLogin('login')}
              onCheckBatchStatus={checkBatchStatus}
              onSwitchRole={handleSwitchRole}
              onLogout={handleLogout}
              allowedCategories={CONSUMABLES_CATEGORIES}
              savedItems={savedItems}
              onToggleSave={toggleSaveItem}
            />
          } />

          <Route path="/rebuilds" element={
            <RebuildMarketplacePage
              key="rebuilds"
              pageTitle="Equipment Rebuilds"
              pageSubtitle="Source base machines and configure your custom rebuild scope"
              onViewProduct={handleViewProduct}
              user={user}
              basket={basket}
              onAddToBasket={addToBasket}
              onUpdateBasketQuantity={updateBasketQuantity}
              onRemoveFromBasket={removeFromBasket}
              onShowLogin={() => handleShowLogin('login')}
            />
          } />

          <Route path="/rentals" element={
            <MarketplacePage
              key="rentals"
              pageTitle="Equipment Rentals"
              pageSubtitle="Rent equipment for your operational needs"
              category="rental-equipment"
              onViewProduct={handleViewProduct}
              user={user}
              basket={basket}
              onAddToBasket={addToBasket}
              onUpdateBasketQuantity={updateBasketQuantity}
              onRemoveFromBasket={removeFromBasket}
              onShowLogin={() => handleShowLogin('login')}
              onCheckBatchStatus={checkBatchStatus}
              onSwitchRole={handleSwitchRole}
              onLogout={handleLogout}
              allowedCategories={RENTAL_CATEGORIES}
              savedItems={savedItems}
            />
          } />

          <Route path="/commercial" element={
            <MarketplacePage
              key="commercial"
              pageTitle="Commercial Services"
              pageSubtitle="Mines, ore sales, and refurbishing services"
              onViewProduct={handleViewProduct}
              user={user}
              basket={basket}
              onAddToBasket={addToBasket}
              onUpdateBasketQuantity={updateBasketQuantity}
              onRemoveFromBasket={removeFromBasket}
              onShowLogin={() => handleShowLogin('login')}
              onCheckBatchStatus={checkBatchStatus}
              onSwitchRole={handleSwitchRole}
              onLogout={handleLogout}
              allowedCategories={COMMERCIAL_CATEGORIES}
              savedItems={savedItems}
              onToggleSave={toggleSaveItem}
            />
          } />

          <Route path="/basket" element={
            <BasketPage
              basket={basket}
              onUpdateQuantity={updateBasketQuantity}
              onRemoveItem={removeFromBasket}
              onClearBasket={clearBasket}
              user={user}
              onShowLogin={() => handleShowLogin('login')}
              onViewProduct={handleViewProductById}
            />
          } />

          <Route path="/components-and-spares" element={
            <WotsMarketplacePage
              onViewProduct={handleViewProduct}
              user={user}
              basket={basket}
              onAddToBasket={addToBasket}
              onUpdateBasketQuantity={updateBasketQuantity}
              onRemoveFromBasket={removeFromBasket}
              onShowLogin={() => handleShowLogin('login')}
              allowedCategories={COMPONENTS_CATEGORIES}
              savedItems={savedItems}
              onToggleSave={toggleSaveItem}
            />
          } />

          <Route path="/kp-insurance" element={
            <KpInsurancePage
              user={user}
              basketCount={basket.reduce((sum, item) => sum + item.quantity, 0)}
              onShowLogin={() => handleShowLogin('login')}
            />
          } />

          <Route path="/financeform" element={
            <FinanceFormPage
              user={user}
              basketCount={basket.reduce((sum, item) => sum + item.quantity, 0)}
            />
          } />

          <Route path="/sheq" element={<SHEQPage />} />

          <Route path="/drill-catalog" element={
            <DrillCatalogPage
              basket={basket}
              onAddToBasket={addToBasket}
              onUpdateBasketQuantity={updateBasketQuantity}
              onRemoveFromBasket={removeFromBasket}
              user={user}
              onShowLogin={() => handleShowLogin('login')}
            />
          } />

          <Route path="/labour" element={<LabourPage />} />

          <Route path="/dashboard" element={
            <ProtectedRoute user={user} allowedRoles={['buyer', 'seller', 'admin']}>
              <DashboardPage
                user={user}
                onViewProductById={handleViewProductById}
                onSwitchRole={handleSwitchRole}
                onLogout={handleLogout}
                onUserUpdate={handleUserUpdate}
              />
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute user={user} allowedRoles={['buyer', 'seller', 'admin']}>
              <ProfilePage
                user={user}
                onUserUpdate={handleUserUpdate}
                installPromptEvent={deferredPrompt}
                onInstallApp={handleInstallPwa}
              />
            </ProtectedRoute>
          } />

          <Route path="/supplier-analytics" element={
            <ProtectedRoute user={user} allowedRoles={['admin']}>
              <SupplierAnalyticsPage />
            </ProtectedRoute>
          } />

          <Route path="/admin/kg-analytics" element={
            <ProtectedRoute user={user} allowedRoles={['admin']}>
              <KgAnalyticsPage />
            </ProtectedRoute>
          } />

          <Route path="/admin/user-paths" element={
            <ProtectedRoute user={user} allowedRoles={['admin']}>
              <UserPathsPage />
            </ProtectedRoute>
          } />

          <Route path="/admin/kp-insurance" element={
            <ProtectedRoute user={user} allowedRoles={['admin']}>
              <KpInsuranceAdminPage />
            </ProtectedRoute>
          } />

          <Route path="/admin/notifications" element={
            <ProtectedRoute user={user} allowedRoles={['admin']}>
              <div className="container mx-auto p-4"><NotificationManager /></div>
            </ProtectedRoute>
          } />

          <Route path="/admin/test-watermark" element={
            <ProtectedRoute user={user} allowedRoles={['admin']}>
              <TestWatermarkPage />
            </ProtectedRoute>
          } />

          <Route path="/admin/platform-fees" element={
            <ProtectedRoute user={user} allowedRoles={['admin']}>
              <PlatformFeesPage />
            </ProtectedRoute>
          } />

          <Route path="/admin/ad-blocks" element={
            <ProtectedRoute user={user} allowedRoles={['admin']}>
              <AdBlocksPage onViewMedia={setAdBlockMedia} />
            </ProtectedRoute>
          } />

          <Route path="/admin/diesel-quotes" element={
            <ProtectedRoute user={user} allowedRoles={['admin']}>
              <DieselQuotesPage />
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <BottomNavBar
        user={user}
        basketCount={basket.reduce((sum, item) => sum + item.quantity, 0)}
        onToggleMenu={() => setIsMobileMenuOpen(true)}
        onShowChat={() => setShowChatIframe(true)}
        onSwitchRole={handleSwitchRole}
        onShowLogin={() => handleShowLogin('login')}
      />

      <Footer />

      <JumpToTopButton />

      {consentBannerOpen && consent === null && (
        <AnalyticsConsentBanner
          onConsentChange={(value) => {
            setConsentBannerOpen(false)
            if (value === 'granted') {
              grantConsent()
            } else if (value === 'declined') {
              declineConsent()
            }
          }}
        />
      )}

      {showLogin && (
        <LoginModal
          isOpen={showLogin}
          onClose={() => setShowLogin(false)}
          mode={authModalMode}
          onLogin={handleLogin}
          onSwitchMode={setAuthModalMode}
        />
      )}

      {selectedProduct && (
        <FullScreenProductModal
          product={selectedProduct}
          isOpen={!!selectedProduct}
          onClose={handleCloseProduct}
          user={user}
          basket={basket}
          onAddToBasket={addToBasket}
          onUpdateBasketQuantity={updateBasketQuantity}
          onRemoveFromBasket={removeFromBasket}
          onShowLogin={handleShowLogin}
          savedItems={savedItems}
          onToggleSave={toggleSaveItem}
          onShowInquiry={handleShowInquiry}
          onShowDieselQuote={handleShowDieselQuote}
          onShowContactAdmin={handleShowContactAdmin}
          onShowQuoteConfirm={handleShowQuoteConfirm}
        />
      )}

      {showInquiry && inquiryProduct && (
        <InquiryModal
          isOpen={showInquiry}
          onClose={() => setShowInquiry(false)}
          product={inquiryProduct}
          user={user}
        />
      )}

      {showDieselQuote && dieselQuoteProduct && (
        <DieselQuoteModal
          isOpen={showDieselQuote}
          onClose={() => setShowDieselQuote(false)}
          product={dieselQuoteProduct}
          onSendQuote={async (formData) => {
            const response = await fetch('/api/send_diesel_quote.php', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...formData, product_name: dieselQuoteProduct.name, listing_number: dieselQuoteProduct.listing_number })
            })
            const result = await response.json()
            if (!response.ok) throw new Error(result.error || 'Failed to send diesel quote.')
            alert(result.message || 'Diesel quote sent successfully!')
          }}
        />
      )}

      {showContactAdmin && contactAdminProduct && (
        <ContactAdminModal
          isOpen={showContactAdmin}
          onClose={() => setShowContactAdmin(false)}
          product={contactAdminProduct}
          user={user}
        />
      )}

      {showQuoteConfirm && quoteConfirmData && (
        <QuoteConfirmationModal
          isOpen={showQuoteConfirm}
          onClose={() => setShowQuoteConfirm(false)}
          userEmail={user?.email}
          onConfirm={async (email) => {
            const { jsPDF } = window.jspdf
            const doc = new jsPDF()
            const minersMarketLogo = new Image()
            minersMarketLogo.src = '/images/logo/MM - Logo.jpeg'
            await new Promise(resolve => { minersMarketLogo.onload = resolve })

            const productImage = new Image()
            if (quoteConfirmData.product.image_url_1 || quoteConfirmData.product.image_url) {
              productImage.src = quoteConfirmData.product.image_url_1 || quoteConfirmData.product.image_url
              await new Promise(resolve => { productImage.onload = resolve })
            }

            doc.addImage(minersMarketLogo, 'JPEG', 15, 15, 30, 30)
            doc.setFontSize(22)
            doc.setFont('helvetica', 'bold')
            doc.text('OFFICIAL QUOTATION', doc.internal.pageSize.getWidth() / 2, 25, { align: 'center' })
            doc.setFontSize(10)
            doc.setFont('helvetica', 'normal')
            doc.setTextColor(100)
            doc.text(`Quote for: ${user.name}`, 15, 50)
            doc.text(`Date: ${new Date().toLocaleDateString()}`, doc.internal.pageSize.getWidth() - 15, 50, { align: 'right' })

            const price = parseFloat(quoteConfirmData.product.price) || 0
            const vat = price * 0.15
            const total = price + vat
            doc.autoTable({
              head: [['Description', 'Listing #', 'Unit Price', 'VAT (15%)', 'Total']],
              body: [[quoteConfirmData.product.name, quoteConfirmData.product.listing_number, `R ${price.toFixed(2)}`, `R ${vat.toFixed(2)}`, `R ${total.toFixed(2)}`]],
              startY: 60,
              headStyles: { fillColor: [22, 160, 133] },
              styles: { textColor: [33, 33, 33] },
            })

            if (productImage) {
              const imgSize = 50
              const imgX = 15
              const finalY = doc.autoTable.previous.finalY
              let imgY = finalY + 10
              if (imgY + imgSize + 20 > 280) {
                doc.addPage()
                imgY = 20
              }
              doc.addImage(productImage, 'JPEG', imgX, imgY, imgSize, imgSize)
              doc.setFontSize(8)
              doc.setFont('helvetica', 'italic')
              doc.setTextColor(100)
              doc.text('Product Image', imgX, imgY + imgSize + 5)
            }

            doc.addPage()
            let lastPageY = 20
            doc.setFontSize(12)
            doc.setFont('helvetica', 'bold')
            doc.text('Banking Details:', 15, lastPageY)
            doc.setFontSize(10)
            doc.setFont('helvetica', 'normal')
            doc.text('Bank Name: FIRST NATIONAL BANK', 15, lastPageY + 7)
            doc.text('Account Type: GOLD BUSINESS ACCOUNT', 15, lastPageY + 14)
            doc.text('Account Number: 63204739205', 15, lastPageY + 21)
            doc.text('Branch Code: 258155', 15, lastPageY + 28)
            doc.text('Branch Name: FNB POP BRANCH DOLYMPUS', 15, lastPageY + 35)
            doc.text('SWIFT Code: FIRNZAJJ', 15, lastPageY + 42)
            doc.setFont('helvetica', 'bold')
            doc.text('Payment Reference: Please use listing number ' + quoteConfirmData.product.listing_number, 15, lastPageY + 49)

            const pdfData = doc.output('datauristring').split(',')[1]

            const response = await fetch('/api/send_quote_email_async.php', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                recipient_email: email,
                recipient_name: user.name,
                product_id: quoteConfirmData.product.id,
                listing_type: quoteConfirmData.product.listingType,
                product_name: quoteConfirmData.product.name,
                listing_number: quoteConfirmData.product.listing_number,
                pdf_data: pdfData
              })
            })

            const result = await response.json()
            if (!response.ok) throw new Error(result.error || 'Failed to send quote.')
            alert(result.message || 'Quote sent successfully!')
          }}
        />
      )}

      {adBlockMedia && (
        <AdBlockMediaViewer
          media={adBlockMedia}
          onClose={() => setAdBlockMedia(null)}
        />
      )}

      {showImageBlurEditor && imageToEdit && (
        <ImageBlurEditor
          image={imageToEdit}
          onClose={() => {
            setShowImageBlurEditor(false)
            setImageToEdit(null)
          }}
          onSave={(blurredImage) => {
            console.log('Blurred image saved:', blurredImage)
            setShowImageBlurEditor(false)
            setImageToEdit(null)
          }}
        />
      )}

      {showConfirmation && confirmationData && (
        <ConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          title={confirmationData.title}
          message={confirmationData.message}
          onConfirm={confirmationData.onConfirm}
          confirmText={confirmationData.confirmText}
          cancelText={confirmationData.cancelText}
        />
      )}

      {showAccountDeletion && (
        <AccountDeletionModal
          isOpen={showAccountDeletion}
          onClose={() => setShowAccountDeletion(false)}
          user={user}
          onAccountDeleted={handleLogout}
        />
      )}

      {showSubscribePrompt && (
        <SubscribePrompt
          onSubscribe={handleRequestNotificationPermission}
          onDismiss={handleDismissSubscribe}
        />
      )}

      {showInstallPwa && (
        <InstallPwa
          onInstall={handleInstallPwa}
          onDismiss={handleDismissPwa}
        />
      )}

      <ChatbaseIframe
        isOpen={showChatIframe}
        onClose={() => setShowChatIframe(false)}
        user={user}
        onShowLogin={() => handleShowLogin('login')}
        onShowSignup={() => setAuthModalMode('signup')}
      />
    </div>
  )
}

export default App
